import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../../utils/api';
import { useAuth } from '../../../context/AuthContext';

const ClassificationsHardskills = () => {
  const { authUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const availableSessions = authUser?.availableSessions?.map((item) => item) || [];

  const batchId = searchParams.get('batchId') || String(availableSessions[0]?.batchId || '');
  const selectedInstitution = searchParams.get('institutionId') || '';
  const selectedDivision = searchParams.get('divisionInstitutionId') || '';

  // Consolidated loading state
  const [loading, setLoading] = useState({
    initial: true,
    institutions: false,
    divisions: false,
    refreshing: false,
  });

  const [error, setError] = useState(null);
  const [institutions, setInstitutions] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [classifications, setClassifications] = useState([]);
  const [types, setTypes] = useState([]);

  // Types for the "type" badge below, and to detect whether this batch is formula v2
  // (mentors may only add new KPI HS for v1 — see isV2Batch below).
  useEffect(() => {
    if (!batchId) {
      setTypes([]);
      return;
    }
    const getTypes = async () => {
      try {
        const data = await api.getClassificationFinalTypes({ domain: 'HS', batchId });
        setTypes(data || []);
      } catch (err) {
        console.error('Failed to get classification types:', err);
      }
    };
    getTypes();
  }, [batchId]);

  const getTypeName = (classificationTypeId) =>
    types.find((type) => type.id === classificationTypeId)?.name;
  const isV2Batch = !!batchId && types.length > 0;

  // Fetch institutions when batchId changes
  useEffect(() => {
    if (!batchId) {
      setInstitutions([]);
      return;
    }
    const getInstitutions = async () => {
      try {
        setLoading((prev) => ({ ...prev, institutions: true }));
        const data = await api.getInstitutions({ filterBy: 'mentor', batchId });
        setInstitutions(data);
      } catch (err) {
        console.error('Error fetching institutions:', err);
        setError('Gagal memuat lembaga. Silakan coba lagi.');
      } finally {
        setLoading((prev) => ({ ...prev, institutions: false }));
      }
    };
    getInstitutions();
  }, [batchId]);

  // Fetch divisions when batchId or selectedInstitution changes
  useEffect(() => {
    if (!batchId || !selectedInstitution) {
      setDivisions([]);
      return;
    }
    const getDivisions = async () => {
      try {
        setLoading((prev) => ({ ...prev, divisions: true }));
        const data = await api.getInstitutionById({
          id: selectedInstitution,
          batchId,
          filterBy: 'mentor',
        });
        setDivisions(data.divisions || []);
      } catch (err) {
        console.error('Error fetching divisions:', err);
        setError('Gagal memuat divisi. Silakan coba lagi.');
        setDivisions([]);
      } finally {
        setLoading((prev) => ({ ...prev, divisions: false }));
      }
    };
    getDivisions();
  }, [batchId, selectedInstitution]);

  // Fetch classifications: division-specific when a division is selected, otherwise
  // a general batch-scoped list.
  useEffect(() => {
    if (!batchId) {
      setClassifications([]);
      setLoading((prev) => ({ ...prev, initial: false }));
      return;
    }
    const getClassifications = async () => {
      const hasData = classifications?.hardskill?.length > 0;
      setLoading((prev) => ({
        ...prev,
        initial: !hasData,
        refreshing: hasData,
      }));
      setError(null);
      try {
        const data = selectedDivision
          ? await api.getClassificationsHardskillByDivisiLembagaId({ id: selectedDivision })
          : await api.getClassificationsHardskill({
            pageSize: 25,
            page: 1,
            filterBy: 'mentor',
            batchId,
          });
        setClassifications(data);
      } catch (err) {
        console.error('Error fetching classifications:', err);
        setError('Gagal memuat KPI. Silakan coba lagi.');
      } finally {
        setLoading((prev) => ({ ...prev, initial: false, refreshing: false }));
      }
    };
    getClassifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchId, selectedDivision]);

  // Handle batch change — resets institution/division selection
  const handleBatchChange = (e) => {
    setSearchParams({ batchId: e.target.value });
  };

  const onHandleDivisiByInstitutionId = ({ id }) => {
    if (!id) {
      setSearchParams({ batchId });
    } else {
      setSearchParams({ batchId, institutionId: id });
    }
  };

  const onHandleGetClassificationsByDivisiLembagaId = ({ id }) => {
    if (!id) {
      setSearchParams({ batchId, institutionId: selectedInstitution });
    } else {
      setSearchParams({
        batchId,
        institutionId: selectedInstitution,
        divisionInstitutionId: id,
      });
    }
  };

  const hasExistingKPI = classifications?.hardskill?.length > 0;
  const kpiQueryString = `?batchId=${batchId}&institutionId=${selectedInstitution}&divisionInstitutionId=${selectedDivision}`;

  return (
    <div className='card card-body border-0 shadow-sm rounded-4'>
      <div className='d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3 mb-4'>
        <div className='d-flex align-items-center gap-3'>
          <div
            className='bg-primary bg-opacity-10 d-flex align-items-center justify-content-center rounded-3'
            style={{ width: '40px', height: '40px' }}
          >
            <i className='bi bi-clipboard-check text-primary fs-5'></i>
          </div>
          <div>
            <h1 className='h5 fw-bold mb-0 text-dark'>
              Key Performance Indicator (KPI)
            </h1>
            <p
              className='text-muted mb-0 d-none d-lg-block'
              style={{ fontSize: '0.75rem' }}
            >
              Kelola dan tinjau klasifikasi hardskill (KPI) mentee per divisi
              lembaga.
            </p>
          </div>
        </div>

        <div className='d-flex flex-wrap align-items-center gap-2 ms-auto'>
          {/* Batch Filter Selector */}
          <div className='d-flex align-items-center me-1'>
            <label className='me-2 fw-medium text-muted small mb-0 d-none d-xl-inline'>
              Batch:
            </label>
            <div style={{ minWidth: '200px' }}>
              <select
                value={batchId}
                onChange={handleBatchChange}
                className='form-select form-select-sm border-0 shadow-sm'
                style={{ borderRadius: '50rem' }}
                disabled={loading.initial}
              >
                {availableSessions.map((b) => (
                  <option
                    key={b.batchId}
                    value={b.batchId}
                  >
                    {b.programName} - Batch {b.batchName}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Update Button — enabled once a division with existing KPI is selected */}
          <button
            type='button'
            disabled={!selectedDivision || !hasExistingKPI}
            onClick={() =>
              navigate(`/mentor/classifications/${selectedDivision}/update`)
            }
            className='btn btn-outline-primary btn-sm rounded-pill px-3 py-2 shadow-none border fw-bold d-flex align-items-center gap-2'
            style={{ fontSize: '0.8rem' }}
          >
            <i className='bi bi-pencil-square'></i>
            <span>Ubah KPI</span>
          </button>

          {/* Add Button — enabled once a division without existing KPI is selected (v1 batches only) */}
          <button
            type='button'
            disabled={!selectedDivision || hasExistingKPI || isV2Batch}
            onClick={() => navigate(`/mentor/classifications/add${kpiQueryString}`)}
            className='btn btn-primary btn-sm rounded-pill px-3 py-2 border-0 shadow-sm fw-bold d-flex align-items-center gap-2'
            style={{ fontSize: '0.8rem' }}
          >
            <i className='bi bi-plus-circle'></i>
            <span>Tambah KPI</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className='bg-light bg-opacity-50 p-3 mb-4 rounded-4 border border-light-subtle'>
        <div className='row g-3 align-items-center'>
          {/* Lembaga */}
          <div className='col-md-6 col-sm-12'>
            <div className='form-floating'>
              <select
                id='institution'
                className='form-select border-0 shadow-sm'
                value={selectedInstitution}
                disabled={loading.institutions || loading.initial}
                onChange={(e) =>
                  onHandleDivisiByInstitutionId({ id: e.target.value })
                }
                required
              >
                <option value=''>
                  {loading.institutions ? 'Memuat...' : 'Pilih Lembaga'}
                </option>
                {institutions.map((institution, key) => (
                  <option
                    key={key}
                    value={institution.institutionsId}
                  >
                    {institution.institutionsName}
                  </option>
                ))}
              </select>
              <label
                htmlFor='institution'
                className='small'
              >
                Lembaga
              </label>
            </div>
          </div>

          {/* Divisi */}
          <div className='col-md-6 col-sm-12'>
            <div className='form-floating'>
              <select
                id='division'
                className='form-select border-0 shadow-sm'
                value={selectedDivision}
                disabled={!divisions?.length > 0 || loading.divisions}
                onChange={(e) =>
                  onHandleGetClassificationsByDivisiLembagaId({
                    id: e.target.value,
                  })
                }
                required
              >
                <option value=''>
                  {loading.divisions
                    ? 'Memuat divisi...'
                    : !selectedInstitution
                      ? 'Pilih lembaga terlebih dahulu'
                      : divisions?.length === 0
                        ? 'Tidak ada divisi'
                        : 'Pilih Divisi'}
                </option>
                {divisions?.map((division, key) => (
                  <option
                    key={key}
                    value={division.institutionsDivisionId}
                  >
                    {division.divisionName}
                  </option>
                ))}
              </select>
              <label
                htmlFor='division'
                className='small'
              >
                Divisi
              </label>
            </div>
          </div>
        </div>
      </div>

      {isV2Batch && (
        <div
          className='alert alert-warning border-0 shadow-sm rounded-4 mb-4 d-flex align-items-center gap-3'
          role='alert'
        >
          <i className='bi bi-exclamation-triangle-fill fs-5'></i>
          <div className='flex-grow-1 small'>
            Batch ini memakai formula <strong>v2</strong> dengan struktur KPI HS baku yang
            dikelola terpusat oleh Admin. Mentor hanya dapat menambah KPI HS baru untuk
            batch formula <strong>v1</strong> — Anda tetap bisa mengubah KPI yang sudah ada.
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div
          className='alert alert-danger alert-dismissible fade show rounded-4'
          role='alert'
        >
          <i className='bi bi-exclamation-triangle-fill me-2'></i>
          {error}
          <button
            type='button'
            className='btn-close'
            onClick={() => setError(null)}
            aria-label='Close'
          ></button>
        </div>
      )}

      {/* Refreshing Indicator */}
      {loading.refreshing && (
        <div className='text-center my-2'>
          <div className='spinner-border spinner-border-sm text-primary' />
          <span className='ms-2 small text-muted'>Memuat data...</span>
        </div>
      )}

      {loading.initial ? (
        <div className='d-flex flex-column justify-content-center align-items-center py-5'>
          <div
            className='spinner-border text-primary mb-3'
            role='status'
          >
            <span className='visually-hidden'>Loading...</span>
          </div>
          <p className='text-muted small'>Memuat data KPI...</p>
        </div>
      ) : (
        <div className='table-responsive'>
          {!hasExistingKPI ? (
            <div className='text-center py-5'>
              <div className='text-muted mb-3'>
                {!selectedInstitution ? (
                  <i className='bi bi-building fs-1 opacity-25'></i>
                ) : !selectedDivision ? (
                  <i className='bi bi-diagram-3 fs-1 opacity-25'></i>
                ) : (
                  <i className='bi bi-clipboard-x fs-1 opacity-25'></i>
                )}
              </div>
              <h6 className='fw-bold text-dark mb-1'>
                {!selectedInstitution
                  ? 'Pilih Lembaga & Divisi'
                  : !selectedDivision
                    ? 'Pilih Divisi'
                    : 'KPI belum terisi!'}
              </h6>
              <p
                className='text-muted small mb-4 mx-auto'
                style={{ maxWidth: '340px' }}
              >
                {!selectedInstitution
                  ? 'Silakan pilih Lembaga dan Divisi terlebih dahulu untuk memunculkan KPI.'
                  : !selectedDivision
                    ? 'Silakan tentukan Divisi terlebih dahulu untuk melihat daftar KPI.'
                    : 'Belum ada data KPI untuk kombinasi ini. Silakan tambahkan KPI melalui tombol di atas.'}
              </p>
              {selectedDivision && !isV2Batch && (
                <button
                  type='button'
                  onClick={() => navigate(`/mentor/classifications/add${kpiQueryString}`)}
                  className='btn btn-primary rounded-pill px-4 fw-bold'
                >
                  <i className='bi bi-plus-circle me-2'></i>
                  Tambah KPI Baru
                </button>
              )}
            </div>
          ) : (
            <table className='table table-hover align-middle mb-0'>
              <thead className='bg-light text-muted small text-uppercase fw-bold'>
                <tr>
                  <th
                    className='ps-4 py-3 border-0'
                    style={{ width: '50px' }}
                  >
                    #
                  </th>
                  <th className='py-3 border-0'>Lembaga</th>
                  <th className='py-3 border-0'>Divisi</th>
                  <th className='py-3 border-0'>KPI / Klasifikasi</th>
                  <th className='pe-4 py-3 border-0 text-end'>Batch</th>
                </tr>
              </thead>
              <tbody>
                {classifications?.hardskill?.map((classification, key) => (
                  <tr
                    key={key}
                    className='transition-all'
                  >
                    <td className='ps-4 text-muted small'>{key + 1}</td>
                    <td className='py-3'>
                      <div className='fw-bold text-dark'>
                        {classification.institutions.institutionsName}
                      </div>
                    </td>
                    <td className='py-3'>
                      <div className='small text-secondary'>
                        {classification.institutions.divisionName}
                      </div>
                    </td>
                    <td className='py-3'>
                      <div className='small fw-medium text-dark bg-light px-3 py-2 rounded-3 d-inline-block border border-light-subtle'>
                        {classification.classification}
                      </div>
                      {getTypeName(classification.classificationTypeId) && (
                        <span className='badge bg-info bg-opacity-10 text-info border border-info border-opacity-25 rounded-pill px-2 py-1 ms-2 extra-small fw-bold'>
                          Otomatis ({getTypeName(classification.classificationTypeId)})
                        </span>
                      )}
                    </td>
                    <td className='pe-4 py-3 text-end'>
                      <span className='badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25 rounded-pill px-3 py-1 extra-small fw-bold'>
                        Batch {classification.batch.batch}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <style>{`
        .form-select:disabled { cursor: not-allowed; }
        .extra-small { font-size: 0.7rem; }
        .transition-all { transition: all 0.2s ease; }
        .table-hover tbody tr:hover {
          background-color: rgba(var(--bs-primary-rgb), 0.03) !important;
          transform: scale(0.998);
        }
      `}</style>
    </div>
  );
};

export default ClassificationsHardskills;
